import { supabase } from "@/lib/supabase";
import {
  konaseemaIntro,
  konaseemaFacts,
  konaseemaGallery,
  konaseemaBandImages,
  konaseemaSeasons,
  konaseemaActivities,
  konaseemaReach,
  konaseemaMapEmbed,
  type KonaseemaSeason,
  type KonaseemaActivity,
  type KonaseemaReach,
} from "@/mocks/konaseemaData";
import {
  tripPool,
  tripPoolFilters,
  tripPresets,
  type TripPoolItem,
  type TripPreset,
} from "@/mocks/tripPlanner";

export interface KonaseemaGuide {
  intro: string[];
  facts: { icon: string; label: string; value: string }[];
  gallery: { src: string; alt: string }[];
  bandImages: { src: string; alt: string }[];
  seasons: KonaseemaSeason[];
  activities: KonaseemaActivity[];
  reach: KonaseemaReach[];
  mapEmbed: string;
}

export const DEFAULT_KONASEEMA_GUIDE: KonaseemaGuide = {
  intro: konaseemaIntro,
  facts: konaseemaFacts,
  gallery: konaseemaGallery,
  bandImages: konaseemaBandImages,
  seasons: konaseemaSeasons,
  activities: konaseemaActivities,
  reach: konaseemaReach,
  mapEmbed: konaseemaMapEmbed,
};

export interface TripPlannerData {
  pool: TripPoolItem[];
  filters: { id: string; label: string }[];
  presets: TripPreset[];
}

export const DEFAULT_TRIP_PLANNER: TripPlannerData = {
  pool: tripPool,
  filters: tripPoolFilters,
  presets: tripPresets,
};

/* ------------------------- lightweight shared store ------------------------ */

let guideCache: KonaseemaGuide | null = null;
let plannerCache: TripPlannerData | null = null;
let pending: Promise<void> | null = null;
const listeners = new Set<() => void>();

export function getKonaseemaGuide(): KonaseemaGuide {
  return guideCache || DEFAULT_KONASEEMA_GUIDE;
}

export function getTripPlannerData(): TripPlannerData {
  return plannerCache || DEFAULT_TRIP_PLANNER;
}

export function getTripPool(): TripPoolItem[] {
  return getTripPlannerData().pool;
}

export function subscribeGuide(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function loadGuideContent(): Promise<void> {
  if (guideCache && plannerCache) return Promise.resolve();
  if (pending) return pending;
  pending = (async () => {
    try {
      const { data, error } = await supabase
        .from("site_settings")
        .select("key, value")
        .in("key", ["konaseema", "trip_planner"]);
      if (error) throw error;

      const rows = (data as { key: string; value: unknown }[] | null) || [];
      const map = new Map(rows.map((row) => [row.key, row.value]));

      const konaseemaValue = map.get("konaseema");
      if (konaseemaValue && typeof konaseemaValue === "object") {
        guideCache = { ...DEFAULT_KONASEEMA_GUIDE, ...(konaseemaValue as Partial<KonaseemaGuide>) };
      }

      const plannerValue = map.get("trip_planner");
      if (plannerValue && typeof plannerValue === "object") {
        const tp = plannerValue as Partial<TripPlannerData>;
        plannerCache = {
          pool: Array.isArray(tp.pool) && tp.pool.length > 0 ? tp.pool : DEFAULT_TRIP_PLANNER.pool,
          filters:
            Array.isArray(tp.filters) && tp.filters.length > 0
              ? tp.filters
              : DEFAULT_TRIP_PLANNER.filters,
          presets:
            Array.isArray(tp.presets) && tp.presets.length > 0
              ? tp.presets
              : DEFAULT_TRIP_PLANNER.presets,
        };
      }

      listeners.forEach((listener) => listener());
    } catch {
      // Keep the curated defaults so the planner and guide never render blank.
    }
  })();
  return pending;
}

/** Merges a raw stored value with the curated defaults for every guide field. */
export function buildKonaseemaGuide(raw: unknown): KonaseemaGuide {
  const r = (raw || {}) as Partial<KonaseemaGuide>;
  const pick = <K extends keyof KonaseemaGuide>(key: K, fallback: KonaseemaGuide[K]): KonaseemaGuide[K] => {
    const value = r[key];
    if (Array.isArray(fallback)) {
      return (Array.isArray(value) && value.length > 0 ? value : fallback) as KonaseemaGuide[K];
    }
    if (typeof fallback === "string") {
      return (typeof value === "string" && value ? value : fallback) as KonaseemaGuide[K];
    }
    return fallback;
  };
  return {
    intro: pick("intro", DEFAULT_KONASEEMA_GUIDE.intro),
    facts: pick("facts", DEFAULT_KONASEEMA_GUIDE.facts),
    gallery: pick("gallery", DEFAULT_KONASEEMA_GUIDE.gallery),
    bandImages: pick("bandImages", DEFAULT_KONASEEMA_GUIDE.bandImages),
    seasons: pick("seasons", DEFAULT_KONASEEMA_GUIDE.seasons),
    activities: pick("activities", DEFAULT_KONASEEMA_GUIDE.activities),
    reach: pick("reach", DEFAULT_KONASEEMA_GUIDE.reach),
    mapEmbed: pick("mapEmbed", DEFAULT_KONASEEMA_GUIDE.mapEmbed),
  };
}

/** Merges a raw stored value with the curated defaults for the trip planner. */
export function buildTripPlannerData(raw: unknown): TripPlannerData {
  const r = (raw || {}) as Partial<TripPlannerData>;
  return {
    pool: Array.isArray(r.pool) && r.pool.length > 0 ? r.pool : DEFAULT_TRIP_PLANNER.pool,
    filters: Array.isArray(r.filters) && r.filters.length > 0 ? r.filters : DEFAULT_TRIP_PLANNER.filters,
    presets: Array.isArray(r.presets) && r.presets.length > 0 ? r.presets : DEFAULT_TRIP_PLANNER.presets,
  };
}

/** Seeds the in-memory store right after an admin save so the live site updates. */
export function primeGuideContent(update: {
  konaseema?: KonaseemaGuide;
  tripPlanner?: TripPlannerData;
}): void {
  if (update.konaseema) guideCache = update.konaseema;
  if (update.tripPlanner) plannerCache = update.tripPlanner;
  listeners.forEach((listener) => listener());
}